import { Button, Form, Spinner, Row, Col, Badge } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import AsyncSelect from "react-select/async";
import React, { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCodeBranch, faCirclePlus, faCircleInfo, faScaleBalanced, faTriangleExclamation, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { getProductList } from "../Controller/ProductController";
import { debounce } from "lodash";

const DAMAGE_CAUSES = [
  { value: 'TRANSIT_DAMAGE', label: '🚚 Transit Damage / Breakage' },
  { value: 'TEMPERATURE_FAILURE', label: '❄️ Cold-Chain / Thawed / Temperature Failure' },
  { value: 'SPOILED', label: '🥑 Spoilage / Freshness Loss' },
  { value: 'SHORT_SUPPLY', label: '📦 Short Supply / Missing from Dispatch' },
  { value: 'CONTAMINATION', label: '⚠️ Contamination / Leakage' },
  { value: 'OTHER', label: '📝 Other Physical Loss' }
];

const VerifyProductForm = ({
  onSubmit,
  onCancel,
  initialQuantity,
  productData,
  loading = false
}) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      quantity: initialQuantity ? initialQuantity.toString() : "0.01",
      product: null,
      update_purchase_cost: false,
    },
  });

  const product = watch('product');
  const inputValueRef = useRef('');
  // When the API already matched a destination store product, lock the form to merge mode
  const isExistingProduct = !!productData?.to_store_product;
  const [mergeWithExisting, setMergeWithExisting] = useState(isExistingProduct);
  const [defaultProductOptions, setDefaultProductOptions] = useState([]);
  const [autoMatchedProduct, setAutoMatchedProduct] = useState(null);
  const [searchingMatchingProduct, setSearchingMatchingProduct] = useState(false);
  const totalDispatched = parseFloat(initialQuantity || productData?.quantity || 0);
  const [quantity, setQuantity] = useState(initialQuantity ? initialQuantity.toString() : '0.01');
  const [quantityError, setQuantityError] = useState('');
  // const [damagedError, setDamagedError] = useState('');

  // Receiving UOM selection driven by the transfer product's uom_mappings (matched by uom_id)
  const transferMappings = Array.isArray(productData?.uom_mappings) ? productData.uom_mappings : [];
  const dispatchedUomCode = (productData?.unit_of_measure || '').trim();
  const dispatchedUomId = productData?.uom_id ? Number(productData.uom_id) : null;
  const findMappingById = (id) =>
    transferMappings.find((u) => Number(u.uom_id || u.id) === Number(id));
  const findMappingByCode = (code) =>
    transferMappings.find((u) =>
      (u.unit_code || u.symbol || u.label || '').trim().toLowerCase() === String(code || '').trim().toLowerCase());
  const mappingCode = (u) => (u ? (u.unit_code || u.symbol || u.label || '') : '');
  const mappingFactor = (u) => (u?.conversion_factor != null ? (parseFloat(u.conversion_factor) || 1) : 1);
  const dispatchedMapping = (dispatchedUomId && findMappingById(dispatchedUomId)) || findMappingByCode(dispatchedUomCode);
  const dispatchedUomFactor = mappingFactor(dispatchedMapping);

  const [selectedUomId, setSelectedUomId] = useState(() => {
    if (dispatchedMapping) return Number(dispatchedMapping.uom_id || dispatchedMapping.id);
    if (transferMappings[0]) return Number(transferMappings[0].uom_id || transferMappings[0].id);
    return dispatchedUomId;
  });
  const matchedUomMapping = findMappingById(selectedUomId);
  const selectedUomCode = mappingCode(matchedUomMapping) || dispatchedUomCode;
  const selectedUomFactor = mappingFactor(matchedUomMapping);
  // Dispatched quantity expressed in the selected UOM (1 unit = conversion_factor base units)
  const totalDispatchedInSelectedUom = selectedUomFactor > 0
    ? totalDispatched * (dispatchedUomFactor / selectedUomFactor)
    : totalDispatched;

  // Damage & Discrepancy states
  const [damagedQuantity, setDamagedQuantity] = useState('0');
  const [damagedError, setDamagedError] = useState('');
  const [damagedUomId, setDamagedUomId] = useState(selectedUomId);
  const [damageCause, setDamageCause] = useState('TRANSIT_DAMAGE');
  const [damageNotes, setDamageNotes] = useState('');
  const [autoCreateWastage, setAutoCreateWastage] = useState(true);
  const [showDamageSection, setShowDamageSection] = useState(false);
  const matchedDamagedMapping = findMappingById(damagedUomId);
  const damagedUomCode = mappingCode(matchedDamagedMapping) || selectedUomCode;
  const damagedUomFactor = mappingFactor(matchedDamagedMapping);

  const acceptedNum = parseFloat(quantity || 0);
  // Remaining unaccounted quantity in base units (accepted qty is in the receiving UOM)
  const remainingBaseQty = Number.isFinite(acceptedNum)
    ? (totalDispatched * dispatchedUomFactor) - (acceptedNum * selectedUomFactor)
    : 0;

  // UOM mismatch between store (base) product and stock transfer product (compared by uom_id)
  const [conversionFactor, setConversionFactor] = useState('');
  const [conversionError, setConversionError] = useState('');
  const transferUom = selectedUomCode || dispatchedUomCode;
  const storeUom = (product?.unit_of_measure || '').trim();
  const transferUomId = selectedUomId;
  const storeUomId = product?.uom_id ? Number(product.uom_id) : null;
  // All UOM ids the store product supports (base + mapped units)
  const storeMappings = Array.isArray(product?.uom_mappings) ? product.uom_mappings : [];
  const storeUomIds = storeMappings
    .map((m) => Number(m.uom_id || m.id))
    .filter(Number.isFinite);
  if (storeUomId) storeUomIds.push(storeUomId);
  // Mismatch only when the transfer UOM is not available on the store product at all
  const uomMismatch =
    mergeWithExisting &&
    !isExistingProduct &&
    !!product?.value &&
    !product?.isNew &&
    !!transferUomId &&
    !storeUomIds.includes(transferUomId);

  // Auto-fill damaged qty so accepted + damaged = dispatched (in base units)
  const syncDamagedFromAccepted = (acc, accFactor, dmgFactor) => {
    if (!Number.isFinite(acc)) return;
    const remBase = (totalDispatched * dispatchedUomFactor) - (acc * accFactor);
    const dmgF = dmgFactor > 0 ? dmgFactor : 1;
    if (remBase > 0) {
      setDamagedQuantity((remBase / dmgF).toFixed(2));
      setShowDamageSection(true);
    } else {
      setDamagedQuantity('0');
      setShowDamageSection(false);
    }
  };

  const handleAcceptedQtyChange = (val) => {
    const regex = /^\d{0,8}(\.\d{0,2})?$/;
    if (regex.test(val) || val === '') {
      setQuantity(val);
      const acc = parseFloat(val || 0);
      if (Number.isFinite(acc) && acc * selectedUomFactor - (totalDispatched * dispatchedUomFactor) > 0.000001) {
        setQuantityError(`Cannot exceed dispatched quantity (${Number(totalDispatchedInSelectedUom.toFixed(4))} ${selectedUomCode})`);
      } else {
        setQuantityError('');
      }
      syncDamagedFromAccepted(acc, selectedUomFactor, damagedUomFactor);
    }
  };

  // Auto-adjust accepted qty when damaged qty changes so the sum matches dispatched
  const handleDamagedQtyChange = (val) => {
    const regex = /^\d{0,8}(\.\d{0,2})?$/;
    if (regex.test(val) || val === '') {
      setDamagedQuantity(val);
      const dmgVal = parseFloat(val || 0);
      if (!Number.isFinite(dmgVal)) return;
      const dmgF = damagedUomFactor > 0 ? damagedUomFactor : 1;
      const dmgBase = dmgVal * dmgF;
      const dispatchedBase = totalDispatched * dispatchedUomFactor;
      const accBase = dispatchedBase - dmgBase;
      const accNew = selectedUomFactor > 0 ? accBase / selectedUomFactor : accBase;
      setQuantity(accNew > 0 ? String(Number(accNew.toFixed(4))) : '0');
      setQuantityError('');
      if (dmgBase - dispatchedBase > 0.000001) {
        setDamagedError(`Cannot exceed ${Number((dispatchedBase / dmgF).toFixed(2))} ${damagedUomCode}`);
      } else {
        setDamagedError('');
      }
    }
  };

  // Fetch products based on search input
  const fetchProductOptions = useCallback(async (searchTerm) => {
    try {
      const response = await getProductList({
        limit: 15,
        offset: 0,
        search: searchTerm?.trim() || '',
        store_id: localStorage.getItem('store_id'),
        own_store: localStorage.getItem('store_id')
      });

      if (response.success && response.data) {
        return response.data.map((p) => {
          const mappings = Array.isArray(p.uom_mappings) ? p.uom_mappings : [];
          const baseMapping = mappings.find(u => u.is_base === 1 || u.is_base === true) || mappings[0];
          const baseUom = baseMapping
            ? (baseMapping.unit_code || baseMapping.symbol || baseMapping.label)
            : (p.unit_of_measure || '');
          const baseUomId = baseMapping
            ? (baseMapping.uom_id || baseMapping.id)
            : (p.base_uom_id || p.uom_id || null);

          return {
            value: p.slack,
            label: baseUom ? `${p.name} (${baseUom})` : p.name,
            name: p.name,
            product_code: p.product_code,
            unitPrice: p.purchase_amount_excluding_tax,
            tax: p?.TaxCode?.total_tax_percentage,
            unit_of_measure: baseUom,
            uom_id: baseUomId ? Number(baseUomId) : null,
            uom_mappings: mappings,
          };
        });
      }
      return [];
    } catch (e) {
      console.error('Error loading products:', e);
      return [];
    }
  }, []);

  // Auto-search target store inventory for existing matching products on mount
  useEffect(() => {
    let isMounted = true;
    const autoLookupProduct = async () => {
      // If already linked by backend to_store_product, skip auto-search
      if (productData?.to_store_product) {
        return;
      }

      const searchName = (productData?.product_name || '').trim();
      const searchCode = (productData?.product_code || '').trim();
      const queryTerm = searchName || searchCode;

      setSearchingMatchingProduct(true);
      try {
        const options = await fetchProductOptions(queryTerm);
        if (!isMounted) return;

        setDefaultProductOptions(options);

        if (options && options.length > 0) {
          const clean = (s) => (s || '').trim().toLowerCase();
          const targetName = clean(searchName);
          const targetCode = clean(searchCode);

          // Find exact match by product name or product code, or single candidate
          const matched = options.find((opt) => {
            const optName = clean(opt.name);
            const optCode = clean(opt.product_code);
            return (
              (targetName && optName === targetName) ||
              (targetCode && optCode && optCode === targetCode)
            );
          }) || (options.length === 1 ? options[0] : null);

          if (matched) {
            setMergeWithExisting(true);
            setValue('product', matched, { shouldValidate: true, shouldDirty: true });
            setAutoMatchedProduct(matched);
          }
        }
      } catch (err) {
        console.error('Error during auto-lookup for matching product in target store:', err);
      } finally {
        if (isMounted) setSearchingMatchingProduct(false);
      }
    };

    autoLookupProduct();
    return () => {
      isMounted = false;
    };
  }, [productData?.product_name, productData?.product_code, productData?.to_store_product, fetchProductOptions, setValue]);

  // Debounced search with 400ms delay
  const debouncedFetch = useMemo(
    () => debounce((inputValue, callback) => {
      const term = inputValue?.trim() ? inputValue : (productData?.product_name || '');
      fetchProductOptions(term).then(callback);
    }, 400),
    [fetchProductOptions, productData?.product_name]
  );

  const handleFormSubmit = (data) => {
    const n = parseFloat(quantity);
    if (!Number.isFinite(n) || n <= 0) {
      setQuantityError('Enter an accepted quantity > 0');
      return;
    }
    // Accepted qty cannot exceed the dispatched qty (compared in base units)
    if (remainingBaseQty < -0.000001) {
      setQuantityError(`Accepted quantity cannot exceed dispatched quantity (${Number(totalDispatchedInSelectedUom.toFixed(4))} ${selectedUomCode})`);
      return;
    }
    const dmg = parseFloat(damagedQuantity || 0);
    if (showDamageSection && dmg > 0 && (n + dmg) > totalDispatched + 1e-9) {
      setDamagedError(`Accepted (${n}) + damaged (${dmg}) cannot exceed dispatched (${totalDispatched})`);
      return;
    }

    // Accepted + damaged must equal the dispatched qty (in base units)
    if (showDamageSection) {
      const dmgFactor = damagedUomFactor > 0 ? damagedUomFactor : 1;
      const sumBase = (n * selectedUomFactor) + (dmg * dmgFactor);
      const dispatchedBase = totalDispatched * dispatchedUomFactor;
      if (Math.abs(sumBase - dispatchedBase) > 0.000001) {
        setDamagedError(`Accepted + damaged must equal dispatched quantity (${Number(totalDispatchedInSelectedUom.toFixed(4))} ${selectedUomCode})`);
        return;
      }
    }
    setDamagedError('');

    if (uomMismatch) {
      const cf = parseFloat(conversionFactor);
      if (!Number.isFinite(cf) || cf <= 0) {
        setConversionError('Enter a valid conversion factor greater than 0');
        return;
      }
    }

    const basePayload = {
      quantity: n,
      uom_id: selectedUomId || null,
      unit_of_measure: selectedUomCode || null,
      conversion_factor: selectedUomFactor,
      damaged_quantity: showDamageSection ? dmg : 0,
      damaged_uom_id: showDamageSection ? (damagedUomId || null) : null,
      damaged_unit_of_measure: showDamageSection ? (damagedUomCode || null) : null,
      damage_cause: damageCause,
      damage_notes: damageNotes,
      auto_create_wastage: autoCreateWastage,
      update_purchase_cost: !!data.update_purchase_cost,
    };

    // For an auto-matched destination product the merge target is fixed
    const effectiveProduct = isExistingProduct
      ? {
        value: productData?.to_store_product?.slack || '',
        label: productData?.to_store_product?.name || productData?.product_name,
        isNew: false,
      }
      : data.product;

    // If not merging, treat the incoming product as a new product
    const formattedData = !mergeWithExisting
      ? {
        ...basePayload,
        product: {
          value: "",
          label: productData?.product_name,
          isNew: true,
        },
        saveAsNew: true,
      }
      : {
        ...basePayload,
        product: {
          ...effectiveProduct,
          value: effectiveProduct?.isNew ? "" : effectiveProduct?.value,
        },
        saveAsNew: effectiveProduct?.isNew || false,
        ...(uomMismatch && {
          conversion_factor: parseFloat(conversionFactor),
        }),
      };

    onSubmit?.(formattedData);
  };

  const validateProduct = (value) => {
    // Auto-matched destination product doesn't need a manual selection
    if (isExistingProduct) {
      return true;
    }
    // In merge mode, must have a real selected product (not a typed-only value)
    if (mergeWithExisting) {
      if (!value?.value || value?.isNew) {
        return "Please select a product from the list";
      }
      return true;
    }
    return true;
  };

  const handleProductChange = (option, field) => {
    if (option) {
      field.onChange(option);
      inputValueRef.current = option.label;
    } else {
      field.onChange(null);
      inputValueRef.current = '';
    }
  };

  const handleInputChange = (newValue) => {
    inputValueRef.current = newValue;
  };

  // Clear pending timeout if the form unmounts
  useEffect(() => {
    return () => { };
  }, []);

  // Reset conversion factor whenever the merge target product or merge mode changes
  useEffect(() => {
    setConversionFactor('');
    setConversionError('');
  }, [product?.value, mergeWithExisting]);

  const handleMenuClose = (field) => {
    // Do not auto-create product on menu close; require explicit selection
  };

  return (
    <Form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Merge choice checkbox */}
      <Form.Group controlId="mergeWithExisting" className="mb-3">
        <Form.Check
          type="checkbox"
          checked={mergeWithExisting}
          disabled={isExistingProduct}
          onChange={(e) => setMergeWithExisting(e.target.checked)}
          label={
            isExistingProduct ? (
              <>
                <span style={{ fontStyle: 'italic', color: 'rgb(40, 167, 69)' }}>
                  {productData?.product_name}
                </span>
                {' '}already exists in store inventory — it will be merged with the matched product.
              </>
            ) : (
              <>
                Do you want to merge{' '}
                <span style={{ fontStyle: 'italic', color: 'rgb(40, 167, 69)' }}>
                  {productData?.product_name}
                </span>
                {' '}with an existing product in store inventory?
              </>
            )
          }
        />
      </Form.Group>

      {/* Product Search */}
      <Form.Group controlId="product" className="mb-3">
        {isExistingProduct ? (
          <Form.Label className="fw-semibold">
            <FontAwesomeIcon icon={faCodeBranch} className="me-2 text-primary" />
            Existing Product
          </Form.Label>
        ) : mergeWithExisting ? (
          <Form.Label className="fw-semibold">
            <FontAwesomeIcon icon={faCodeBranch} className="me-2 text-primary" />
            Merge with existing product
          </Form.Label>
        ) : (
          <Form.Label className="fw-semibold">
            <FontAwesomeIcon icon={faCirclePlus} className="me-2 text-success" />
            New Product
          </Form.Label>
        )}

        {isExistingProduct ? (
          <div
            className="d-flex align-items-center px-3 py-2 rounded"
            style={{
              background: '#f0f6ff',
              border: '1px dashed #0d6efd',
              fontWeight: 600,
              color: '#0d6efd',
            }}
          >
            {productData?.to_store_product?.name || productData?.product_name || ''}
          </div>
        ) : mergeWithExisting ? (
          <>
            {searchingMatchingProduct && (
              <div className="text-muted d-flex align-items-center mb-2" style={{ fontSize: '0.8rem' }}>
                <Spinner animation="border" size="sm" className="me-2" />
                Searching target store inventory for matching products...
              </div>
            )}

            {autoMatchedProduct && !isExistingProduct && (
              <div
                className="d-flex align-items-center justify-content-between p-2 mb-2 rounded"
                style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', fontSize: '0.825rem' }}
              >
                <div>
                  <FontAwesomeIcon icon={faScaleBalanced} className="me-2 text-success" />
                  <span className="fw-semibold text-success">Matching product found in target store:</span>{' '}
                  <strong>{autoMatchedProduct.name || autoMatchedProduct.label}</strong>
                </div>
                <Badge bg="success">Auto-Matched</Badge>
              </div>
            )}

            <Controller
              control={control}
              name="product"
              rules={{ validate: validateProduct }}
              render={({ field }) => (
                <AsyncSelect
                  {...field}
                  placeholder="Type to search for an existing product..."
                  cacheOptions={false}
                  defaultOptions={defaultProductOptions.length > 0 ? defaultProductOptions : true}
                  loadOptions={debouncedFetch}
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: errors.product ? '#dc3545' : base.borderColor,
                    }),
                  }}
                  onChange={(option) => handleProductChange(option, field)}
                  onInputChange={handleInputChange}
                  onMenuClose={() => handleMenuClose(field)}
                  onBlur={() => {
                    field.onBlur();
                  }}
                />
              )}
            />

            {errors.product && (
              <div className="invalid-feedback d-block">
                {errors.product.message}
              </div>
            )}
          </>
        ) : (
          <div
            className="d-flex align-items-center px-3 py-2 rounded"
            style={{
              background: '#f8f9fa',
              border: '1px dashed #adb5bd',
              fontWeight: 600,
              color: '#198754',
            }}
          >
            {productData?.product_name || ''}
          </div>
        )}
      </Form.Group>

      {/* Dispatched Reference vs Accepted Quantity */}
      <div className="p-2 mb-3 bg-light rounded border">
        <Row className="g-2 align-items-center">
          <Col xs={6}>
            <small className="text-muted d-block font-weight-bold">DISPATCHED QUANTITY</small>
            <span className="fs-6 font-weight-bold text-dark">
              {totalDispatched.toFixed(2)}{dispatchedUomCode ? ` ${dispatchedUomCode}` : ''}
            </span>
            {selectedUomCode && dispatchedUomCode && selectedUomCode !== dispatchedUomCode && (
              <small className="text-muted d-block">
                ≈ {Number(totalDispatchedInSelectedUom.toFixed(4))} {selectedUomCode}
              </small>
            )}
          </Col>
          <Col xs={6}>
            <small className="text-primary d-block font-weight-bold">
              <FontAwesomeIcon icon={faScaleBalanced} className="me-1" /> ACCEPTED GOOD STOCK
              {selectedUomCode ? ` (${selectedUomCode})` : ''}
            </small>
            <Form.Control
              type="text"
              size="sm"
              className="text-end font-weight-bold text-success"
              value={quantity}
              onChange={(e) => handleAcceptedQtyChange(e.target.value)}
              isInvalid={!!quantityError}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'Minus') e.preventDefault();
              }}
              onPaste={(e) => {
                const t = (e.clipboardData || window.clipboardData).getData('text');
                if (t && t.includes('-')) e.preventDefault();
              }}
            />
          </Col>
        </Row>
        {transferMappings.length > 0 && (
          <Row className="g-2 align-items-center mt-2">
            <Col xs={6}>
              <small className="text-muted d-block font-weight-bold">RECEIVING UOM</small>
            </Col>
            <Col xs={6}>
              <Form.Select
                size="sm"
                value={selectedUomId ?? ''}
                disabled={transferMappings.length <= 1}
                onChange={(e) => {
                  const newId = Number(e.target.value);
                  const newFactor = mappingFactor(findMappingById(newId)) || 1;
                  setSelectedUomId(newId);
                  // Keep damaged UOM aligned with the receiving UOM
                  setDamagedUomId(newId);
                  syncDamagedFromAccepted(parseFloat(quantity || 0), newFactor, newFactor);
                }}
              >
                {transferMappings.map((u) => {
                  const id = Number(u.uom_id || u.id);
                  const code = u.unit_code || u.symbol || u.label;
                  return (
                    <option key={id} value={id}>
                      {u.label || code} ({code}){(u.is_base === 1 || u.is_base === true) ? ' (Base)' : ''}
                    </option>
                  );
                })}
              </Form.Select>
            </Col>
          </Row>
        )}
        {quantityError && (
          <div className="invalid-feedback d-block text-end mt-1">
            <span className="text-danger">{quantityError}</span>
          </div>
        )}
      </div>

      {/* UOM mismatch: conversion factor from stock transfer UOM to store (base) product UOM */}
      {uomMismatch && (
        <div
          className="p-3 mb-3 rounded border"
          style={{ background: '#fffbeb', borderColor: '#f59e0b' }}
        >
          <Form.Label className="fw-semibold d-block mb-2">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" style={{ color: '#d97706' }} />
            Unit of measure mismatch — enter conversion factor
          </Form.Label>
          <div className="d-flex align-items-center gap-2">
            <span className="text-nowrap fw-semibold">
              1 {transferUom || 'transfer unit'} =
            </span>
            <Form.Control
              type="text"
              size="sm"
              className="text-end fw-semibold"
              style={{ maxWidth: 140 }}
              value={conversionFactor}
              placeholder="e.g. 1000"
              isInvalid={!!conversionError}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d{0,8}(\.\d{0,6})?$/.test(val) || val === '') {
                  setConversionFactor(val);
                  setConversionError('');
                }
              }}
            />
            <span className="text-nowrap fw-semibold">{storeUom || 'store unit'}</span>
          </div>
          <small className="text-muted d-block mt-1">
            Stock transfer UOM ({transferUom || '-'}) to store product UOM ({storeUom || '-'})
          </small>
          {Number.isFinite(parseFloat(conversionFactor)) && parseFloat(conversionFactor) > 0 && (
            <div className="mt-1 small fw-semibold text-success">
              Total transfer qty {totalDispatched} {transferUom} will be converted to{' '}
              {Number((totalDispatched * parseFloat(conversionFactor)).toFixed(4))} {storeUom}
            </div>
          )}
          {conversionError && (
            <div className="invalid-feedback d-block mt-1">
              <span className="text-danger">{conversionError}</span>
            </div>
          )}
        </div>
      )}

      {/* Transit Loss / Damage Section Toggle */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Check
            type="checkbox"
            id="showDamageSection"
            checked={showDamageSection}
            disabled={remainingBaseQty <= 0}
            onChange={(e) => setShowDamageSection(e.target.checked)}
            label={
              <span className="fw-semibold text-danger">
                <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" /> Record Damaged / Missing Transit Quantity
              </span>
            }
          />
          {showDamageSection && parseFloat(damagedQuantity || 0) > 0 && (
            <Badge bg="danger">-{parseFloat(damagedQuantity).toFixed(2)} Loss</Badge>
          )}
        </div>

        {showDamageSection && (
          <div className="p-3 bg-danger bg-opacity-10 border border-danger rounded mt-2">
            <Row className="g-2 mb-2">
              <Col md={transferMappings.length > 0 ? 4 : 5}>
                <Form.Label className="small font-weight-bold text-danger mb-1">
                  Damaged / Missing Qty{damagedUomCode ? ` (${damagedUomCode})` : ''}
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  className="text-end font-weight-bold text-danger"
                  value={damagedQuantity}
                  isInvalid={!!damagedError}
                  onChange={(e) => handleDamagedQtyChange(e.target.value)}
                  placeholder="0.00"
                />
                {damagedError && (
                  <div className="invalid-feedback d-block" style={{ fontSize: '0.7rem' }}>
                    {damagedError}
                  </div>
                )}
              </Col>
              {transferMappings.length > 0 && (
                <Col md={3}>
                  <Form.Label className="small font-weight-bold text-dark mb-1">Unit</Form.Label>
                  <Form.Select
                    size="sm"
                    value={damagedUomId ?? ''}
                    disabled={transferMappings.length <= 1}
                    onChange={(e) => {
                      const newId = Number(e.target.value);
                      const newFactor = mappingFactor(findMappingById(newId)) || 1;
                      const cur = parseFloat(damagedQuantity || 0);
                      // Convert the entered damaged qty into the newly selected unit
                      if (Number.isFinite(cur) && cur > 0 && damagedUomFactor > 0) {
                        setDamagedQuantity((cur * (damagedUomFactor / newFactor)).toFixed(2));
                      }
                      setDamagedUomId(newId);
                    }}
                  >
                    {transferMappings.map((u) => {
                      const id = Number(u.uom_id || u.id);
                      const code = u.unit_code || u.symbol || u.label;
                      return (
                        <option key={id} value={id}>
                          {code}{(u.is_base === 1 || u.is_base === true) ? ' (Base)' : ''}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Col>
              )}
              <Col md={transferMappings.length > 0 ? 5 : 7}>
                <Form.Label className="small font-weight-bold text-dark mb-1">Damage / Loss Reason</Form.Label>
                <Form.Select
                  size="sm"
                  value={damageCause}
                  onChange={(e) => setDamageCause(e.target.value)}
                >
                  {DAMAGE_CAUSES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label className="small text-muted mb-1">Damage Investigation Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                placeholder="e.g. 2 cartons crushed during road transit / seals broken..."
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
              />
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="autoCreateWastage"
              checked={autoCreateWastage}
              onChange={(e) => setAutoCreateWastage(e.target.checked)}
              label={
                <small className="fw-semibold text-dark">
                  <FontAwesomeIcon icon={faTrashCan} className="me-1 text-danger" />
                  Auto-create Transit Wastage Ticket (Direct Write-Off)
                </small>
              }
            />
          </div>
        )}
      </div>

      {/* Update Avg. Purchase Cost Checkbox */}
      <Form.Group controlId="update_purchase_cost" className="mb-3">
        <Controller
          control={control}
          name="update_purchase_cost"
          render={({ field }) => (
            <Form.Check
              {...field}
              type="checkbox"
              id="update_purchase_cost"
              label="Update Avg. Purchase Cost"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />
      </Form.Group>

      <p className="alert alert-warning py-2 px-3 mb-3 small d-flex align-items-start" style={{ borderRadius: '0.5rem' }}>
        <FontAwesomeIcon icon={faCircleInfo} className="me-2 mt-1 flex-shrink-0" />
        <span>
          The accepted quantity will be added to {mergeWithExisting ? 'the selected product' : 'the new product'} and the same
          quantity will be reduced from the source store inventory.
          {showDamageSection && parseFloat(damagedQuantity || 0) > 0 && (
            <strong className="d-block text-danger mt-1">
              ⚠️ {damagedQuantity} damaged units will be logged as a Transfer Discrepancy{autoCreateWastage ? ' & auto-created as a Transit Wastage ticket.' : '.'}
            </strong>
          )}
        </span>
      </p>

      <div className="d-flex justify-content-end gap-2">
        <Button
          variant="outline-secondary"
          onClick={() => {
            onCancel?.();
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Processing...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </Form>
  );
};

export default VerifyProductForm;